import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import StalenessBanner from '@/components/StalenessBanner';
import SecurityPostureCard from '@/components/SecurityPostureCard';
import WatchlistDiffTicker from '@/components/WatchlistDiffTicker';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, Database, Clock, TrendingUp, Search,
  RefreshCw, Zap, BarChart3, PieChart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const getRiskColor = (level) => {
  switch (level) {
    case 'critical': return 'text-rose-700 bg-rose-50 border-rose-200';
    case 'high': return 'text-rose-700 bg-rose-50 border-rose-200';
    case 'medium': return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'low': return 'text-blue-700 bg-blue-50 border-blue-200';
    default: return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  }
};

const getRiskDot = (level) => {
  switch (level) {
    case 'critical': case 'high': return 'bg-rose-500';
    case 'medium': return 'bg-amber-500';
    case 'low': return 'bg-blue-500';
    default: return 'bg-emerald-500';
  }
};

function MiniBarChart({ data, maxBars = 15 }) {
  const sliced = data.slice(-maxBars);
  if (sliced.length === 0) return <div className="text-xs text-slate-400 py-4">No data yet</div>;
  const maxVal = Math.max(...sliced.map(d => d.total), 1);

  return (
    <div className="flex items-stretch gap-[3px] h-28" data-testid="mini-bar-chart">
      {sliced.map((d, i) => {
        const highPct = maxVal > 0 ? (d.high_risk / maxVal) * 100 : 0;
        const clearPct = maxVal > 0 ? (d.clear / maxVal) * 100 : 0;
        const otherPct = maxVal > 0 ? ((d.total - d.high_risk - d.clear) / maxVal) * 100 : 0;
        return (
          <div key={d.date || `bar-${i}`} className="flex-1 h-full flex flex-col justify-end gap-[1px] group relative" title={`${d.date}: ${d.total} scans`}>
            {highPct > 0 && <div className="rounded-t-sm bg-rose-400 transition-all" style={{ height: `${highPct}%`, minHeight: highPct > 0 ? '2px' : 0 }} />}
            {otherPct > 0 && <div className="bg-amber-400 transition-all" style={{ height: `${otherPct}%`, minHeight: otherPct > 0 ? '2px' : 0 }} />}
            {clearPct > 0 && <div className="rounded-b-sm bg-emerald-400 transition-all" style={{ height: `${clearPct}%`, minHeight: clearPct > 0 ? '2px' : 0 }} />}
            {d.total === 0 && <div className="bg-slate-200 rounded-sm" style={{ height: '2px' }} />}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[8px] text-slate-400 opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
              {d.date.slice(5)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RiskDonut({ breakdown }) {
  const total = Object.values(breakdown).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="text-xs text-slate-400 py-4">No data yet</div>;

  const segments = [
    { key: 'clear', label: 'Clear', color: '#10b981', count: breakdown.clear || 0 },
    { key: 'low', label: 'Low', color: '#3b82f6', count: breakdown.low || 0 },
    { key: 'medium', label: 'Medium', color: '#f59e0b', count: breakdown.medium || 0 },
    { key: 'high', label: 'High', color: '#ef4444', count: breakdown.high || 0 },
    { key: 'critical', label: 'Critical', color: '#991b1b', count: breakdown.critical || 0 },
  ].filter(s => s.count > 0);

  let offset = 0;
  const radius = 36;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="flex items-center gap-6" data-testid="risk-donut">
      <svg width="96" height="96" viewBox="0 0 96 96" className="shrink-0">
        {segments.map((seg) => {
          const pct = seg.count / total;
          const dashLen = pct * circumference;
          const dashOffset = -offset * circumference;
          offset += pct;
          return (
            <circle
              key={seg.key}
              cx="48" cy="48" r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth="10"
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 48 48)"
              className="transition-all duration-500"
            />
          );
        })}
        <text x="48" y="48" textAnchor="middle" dominantBaseline="central" className="text-lg font-bold fill-slate-900" fontSize="18">{total}</text>
      </svg>
      <div className="space-y-1">
        {segments.map(seg => (
          <div key={seg.key} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
            <span className="text-slate-600">{seg.label}</span>
            <span className="font-medium text-slate-900">{seg.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [activity, setActivity] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const autoRefreshRef = useRef(null);

  const fetchDashboardData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [statsRes, scansRes, activityRes, trendsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/scans?limit=5`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/activity?limit=15`, { headers: getAuthHeaders() }),
        axios.get(`${API}/dashboard/trends?days=14`, { headers: getAuthHeaders() }),
      ]);
      setStats(statsRes.data);
      setRecentScans(scansRes.data);
      setActivity(activityRes.data.activity || []);
      setTrends(trendsRes.data);
    } catch (error) {
      if (!silent) toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    autoRefreshRef.current = setInterval(() => fetchDashboardData(true), 30000);
    return () => clearInterval(autoRefreshRef.current);
  }, [fetchDashboardData]);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="dashboard">
      <StalenessBanner user={user} />
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-sm text-slate-600 mt-2">Overview of your compliance screening activity</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchDashboardData(true)}
              disabled={refreshing}
              className="border-slate-300"
              data-testid="refresh-dashboard-button"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => navigate('/scan')}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="new-scan-button"
            >
              <Search className="w-4 h-4 mr-2" />
              New Scan
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading dashboard...</div>
        ) : (
          <>
            {/* Security posture (super_admin only) */}
            <SecurityPostureCard user={user} />

            {/* Live watchlist changes (super_admin only) */}
            {user?.role === 'super_admin' && <WatchlistDiffTicker />}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stats-grid">
              <Card className="p-6 border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Scans</p>
                    <p className="text-3xl font-bold text-slate-900 mt-2" data-testid="total-scans">
                      {stats?.total_scans || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                    <Activity className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-xs text-slate-500">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  All time
                </div>
              </Card>

              <Card className="p-6 border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">High Risk</p>
                    <p className="text-3xl font-bold text-rose-600 mt-2" data-testid="high-risk-count">
                      {stats?.high_risk_matches || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-rose-50 rounded-md flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-rose-600" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-xs text-slate-500">
                  <span>Requires attention</span>
                </div>
              </Card>

              <Card className="p-6 border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Scans Today</p>
                    <p className="text-3xl font-bold text-slate-900 mt-2" data-testid="api-calls-today">
                      {stats?.api_calls_today || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-slate-50 rounded-md flex items-center justify-center">
                    <Zap className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-xs text-slate-500">
                  <Clock className="w-3 h-3 mr-1" />
                  Last 24 hours
                </div>
              </Card>

              <Card className="p-6 border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Last Sync</p>
                    <p className="text-sm font-mono text-slate-900 mt-2" data-testid="last-sync">
                      {stats?.last_sync
                        ? new Date(stats.last_sync).toLocaleDateString()
                        : 'Not synced'}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-emerald-50 rounded-md flex items-center justify-center">
                    <Database className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
                <div className="flex items-center mt-4 text-xs text-slate-500">Watchlist data</div>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Screening Volume Chart */}
              <Card className="lg:col-span-2 border border-slate-200 shadow-sm" data-testid="screening-volume-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-slate-600" />
                      <h2 className="text-base font-semibold text-slate-900">Screening Volume</h2>
                    </div>
                    <span className="text-xs text-slate-500">Last {trends?.period_days || 14} days</span>
                  </div>
                </div>
                <div className="p-6">
                  <MiniBarChart data={trends?.daily || []} maxBars={14} />
                  <div className="flex items-center gap-4 mt-6 text-xs text-slate-500">
                    <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-rose-400" /> High Risk</div>
                    <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> Medium/Low</div>
                    <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-400" /> Clear</div>
                  </div>
                </div>
              </Card>

              {/* Risk Breakdown */}
              <Card className="border border-slate-200 shadow-sm" data-testid="risk-breakdown-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-slate-600" />
                    <h2 className="text-base font-semibold text-slate-900">Risk Breakdown</h2>
                  </div>
                </div>
                <div className="p-6">
                  <RiskDonut breakdown={trends?.risk_breakdown || {}} />
                </div>
              </Card>
            </div>

            {/* Credit Consumption (for org users) */}
            {trends?.credit_info && (
              <Card className="border border-slate-200 shadow-sm" data-testid="credit-consumption-card">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Database className="w-5 h-5 text-slate-600" />
                      <h2 className="text-base font-semibold text-slate-900">Credit Consumption</h2>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {trends.credit_info.remaining.toLocaleString()} remaining
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            (trends.credit_info.used / Math.max(trends.credit_info.total, 1)) > 0.9
                              ? 'bg-rose-500'
                              : (trends.credit_info.used / Math.max(trends.credit_info.total, 1)) > 0.75
                                ? 'bg-amber-500'
                                : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min((trends.credit_info.used / Math.max(trends.credit_info.total, 1)) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-mono text-slate-600 shrink-0">
                      {trends.credit_info.used.toLocaleString()} / {trends.credit_info.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </Card>
            )}

            {/* Live Activity + Source Breakdown Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Live Activity Feed */}
              <Card className="lg:col-span-2 border border-slate-200 shadow-sm" data-testid="live-activity-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="w-5 h-5 text-slate-600" />
                      <h2 className="text-base font-semibold text-slate-900">Live Activity</h2>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-xs text-slate-500">Auto-refreshing</span>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                  {activity.length === 0 ? (
                    <div className="p-12 text-center text-slate-500 text-sm">
                      No screening activity yet. Run your first scan to see activity here.
                    </div>
                  ) : (
                    activity.map((item) => (
                      <div
                        key={item.id}
                        className="px-6 py-3.5 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate('/results', { state: { scanId: item.id } })}
                        data-testid={`activity-item-${item.id}`}
                      >
                        <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${getRiskDot(item.risk_level)}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{item.query_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {item.sources.map((s, si) => (
                              <span key={`${s}-${si}`} className="text-[10px] text-slate-500 font-mono">{s}</span>
                            ))}
                            {item.via_api && <Badge className="bg-slate-100 text-slate-600 text-[10px] px-1 py-0">API</Badge>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <Badge className={`text-[10px] ${getRiskColor(item.risk_level)}`}>
                            {item.risk_level}
                          </Badge>
                          <p className="text-[10px] text-slate-400 mt-1">{timeAgo(item.scan_date)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              {/* Source Breakdown */}
              <Card className="border border-slate-200 shadow-sm" data-testid="source-breakdown-card">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-slate-600" />
                    <h2 className="text-base font-semibold text-slate-900">Match Sources</h2>
                  </div>
                </div>
                <div className="p-6 space-y-3">
                  {(!trends?.source_breakdown || trends.source_breakdown.length === 0) ? (
                    <p className="text-xs text-slate-400">No match data yet</p>
                  ) : (
                    trends.source_breakdown.map((src, idx) => {
                      const maxCount = Math.max(...trends.source_breakdown.map(s => s.count), 1);
                      const pct = (src.count / maxCount) * 100;
                      const sourceColors = {
                        'OFAC SDN': 'bg-rose-500',
                        'OpenSanctions PEP': 'bg-blue-500',
                        'MY MOHA Sanctions': 'bg-purple-500',
                        'UNSCR': 'bg-orange-500',
                        'OpenSanctions': 'bg-teal-500',
                      };
                      return (
                        <div key={src.source || `src-${idx}`} data-testid={`source-bar-${idx}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-slate-700 truncate">{src.source}</span>
                            <span className="text-xs font-mono text-slate-500">{src.count}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${sourceColors[src.source] || 'bg-slate-400'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Card>
            </div>

            {/* Recent Scans Table */}
            <Card className="border border-slate-200 shadow-sm">
              <div className="p-6 border-b border-slate-200">
                <h2 className="text-base font-semibold text-slate-900" data-testid="recent-scans-title">
                  Recent Scans
                </h2>
              </div>
              <div className="overflow-x-auto">
                {recentScans.length === 0 ? (
                  <div className="p-12 text-center text-slate-500">
                    No scans yet. Start by scanning a name.
                  </div>
                ) : (
                  <table className="w-full text-sm" data-testid="recent-scans-table">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Risk Level</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Matches</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {recentScans.map((scan) => (
                        <tr
                          key={scan.id}
                          className="hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => navigate('/results', { state: { scanId: scan.id } })}
                          data-testid={`scan-row-${scan.id}`}
                        >
                          <td className="px-6 py-4 font-medium text-slate-900">{scan.query_name}</td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${getRiskColor(scan.risk_level)}`}
                              data-testid={`risk-badge-${scan.risk_level}`}
                            >
                              {scan.risk_level.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-slate-600">{scan.matches.length}</td>
                          <td className="px-6 py-4 font-mono text-xs text-slate-500">
                            {new Date(scan.scan_date).toLocaleString()}
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/results', { state: { scanId: scan.id } });
                              }}
                              data-testid={`view-details-${scan.id}`}
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
